class PromotionItem extends HTMLElement {
  $element = null;
  type = null;
  trigger = null;
  display = null;
  dismiss_delay = null;
  id = null;
  $closeButton = null;

  constructor() {
    super();

    this.type = this.getAttribute('data-type') || 'bar'; // toaster, bar, modal
    this.trigger = this.getAttribute('data-trigger') || 'timer'; // timer, scroll, exit
    this.display = this.getAttribute('data-display') || 'fixed'; // fixed, inline
    this.dismiss_delay = this.getAttribute('data-dismiss-delay') * 1 || 1; // In days
    this.id = this.getAttribute('data-id') || 'promotion-item-' + this.type;
    this.$element = this.querySelector(`promotion-${this.type}`);
    if (!this.$element) {
      console.warn(`PromotionItem: No promotion element found for type ${this.type}`);
      return;
    }
    this.$closeButton = this.$element.querySelector('[data-promotion-closer]');

    /*console.log(
      'PromotionItem',
      this.type,
      this.trigger,
      this.display,
      this.dismiss_delay,
      this.id,
    );*/

    this._onScroll = this.onScroll.bind(this);
    this._onMouseOut = this.onMouseOut.bind(this);
    this._onElementComplete = this.onElementComplete.bind(this);
    this._onElementClose = this.onElementClose.bind(this);
    this._onCloseButtonClick = this.onCloseButtonClick.bind(this);
  }

  connectedCallback() {
    this.setupTrigger();
    this.addEventListeners();
  }
  disconnectedCallback() {
    this.removeTrigger();
    this.removeEventListeners();
  }

  setupTrigger() {
    if (!this.canBeShown) {
      return;
    }
    if (this.trigger === 'timer') {
      const delay = this.getAttribute('data-timer-delay') * 1000 || 1000;
      this._timer = setTimeout(() => {
        this.show();
      }, delay);
    } else if (this.trigger === 'scroll') {
      window.addEventListener('scroll', this._onScroll);
    } else if (this.trigger === 'exit') {
      document.addEventListener('mouseout', this._onMouseOut);
    }
  }

  removeTrigger() {
    if (this.trigger === 'timer' && this._timer) {
      clearTimeout(this._timer);
    } else if (this.trigger === 'scroll' && this._onScroll) {
      window.removeEventListener('scroll', this._onScroll);
    } else if (this.trigger === 'exit' && this._onMouseOut) {
      document.removeEventListener('mouseout', this._onMouseOut);
    }
  }

  onScroll() {
    const scrollPercent = this.getAttribute('data-scroll-percent') * 1 || 1;
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    const scrolled = (scrollTop / docHeight) * 100;
    if (scrolled >= scrollPercent) {
      this.show();
      this.removeTrigger();
    }
  }

  onMouseOut(e) {
    if (e.clientY <= 0) {
      this.show();
      this.removeTrigger();
    }
  }

  onElementComplete() {
    // Handle any actions after the promotion element is completed (e.g., modal closed)
    this.canBeShown = false; // Mark as dismissed
  }
  onElementClose() {
    // Handle any actions when the promotion element is closed (e.g., modal closed)
    this.canBeShown = false; // Mark as dismissed
    this.hide();
  }

  addEventListeners() {
    // Add event listeners here

    this.$element.addEventListener('complete', this._onElementComplete);
    this.$element.addEventListener('close', this._onElementClose);
    this.$closeButton?.addEventListener('click', this._onCloseButtonClick);
  }

  removeEventListeners() {
    // Remove event listeners here
    this.$element.removeEventListener('complete', this._onElementComplete);
    this.$element.removeEventListener('close', this._onElementClose);
    this.$closeButton?.removeEventListener('click', this._onCloseButtonClick);
  }

  get canBeShown() {
    // Check localStorage or cookies to see if the promotion has been dismissed
    const dismissed = localStorage.getItem(`promotion-dismissed-${this.id}`);
    if (dismissed) {
      const dismissedTime = new Date(parseInt(dismissed, 10));
      const now = new Date();
      const diffInDays = (now - dismissedTime) / (1000 * 60 * 60 * 24);
      return diffInDays >= this.dismiss_delay;
    }
    return true;
  }

  set canBeShown(value) {
    if (!value) {
      localStorage.setItem(`promotion-dismissed-${this.id}`, Date.now().toString());
    } else {
      localStorage.removeItem(`promotion-dismissed-${this.id}`);
    }
  }

  show() {
    if (this.canBeShown) {
      this.$element.show();
    }
  }

  hide() {
    this.$element.hide();
    this.canBeShown = false; // Mark as dismissed
  }

  onCloseButtonClick() {
    this.hide();
  }
}

class PromotionElement extends HTMLElement {
  constructor() {
    super();

    this._emitComplete = this.emitComplete.bind(this);
    this._emitClose = this.emitClose.bind(this);
  }

  connectedCallback() {
    this.addEventListeners();
  }
  disconnectedCallback() {
    this.removeEventListeners();
  }

  addEventListeners() {}
  removeEventListeners() {}

  show() {}
  hide() {}

  emitComplete() {
    //console.log('PromotionElement: emitComplete');
    const event = new CustomEvent('complete', { bubbles: true, composed: true });
    this.dispatchEvent(event);
  }
  emitClose() {
    //console.log('PromotionElement: emitClose');
    const event = new CustomEvent('close', { bubbles: true, composed: true });
    this.dispatchEvent(event);
  }
}

class PromotionBar extends PromotionElement {
  $button = null;
  constructor() {
    super();
    this.$button = this.querySelector('.promotion-bar__button button');
  }

  addEventListeners() {
    if (this.$button) {
      this.$button.addEventListener('click', this._emitComplete);
    }
  }
  removeEventListeners() {
    if (this.$button) {
      this.$button.removeEventListener('click', this._emitComplete);
    }
  }

  show() {
    this.removeAttribute('hidden');
  }
  hide() {
    this.setAttribute('hidden', true);
  }
}

customElements.define('promotion-bar', PromotionBar);

class PromotionToaster extends PromotionElement {
  $cardButtons = [];
  constructor() {
    super();
    this.$cardButtons = this.querySelectorAll('.card button, .card a');
  }

  addEventListeners() {
    this.$cardButtons.forEach($btn => {
      $btn.addEventListener('click', this._emitComplete);
    });
  }
  removeEventListeners() {
    this.$cardButtons.forEach($btn => {
      $btn.removeEventListener('click', this._emitComplete);
    });
  }

  show() {
    this.removeAttribute('hidden');
  }
  hide() {
    this.setAttribute('hidden', true);
  }
}

customElements.define('promotion-toaster', PromotionToaster);

class PromotionModal extends PromotionElement {
  $modal = null;
  $embedMarketo = null;

  constructor() {
    super();

    this.$modal = this.querySelector('.site-modal');
    this.$embedMarketo = this.$modal.querySelector('marketo-form');
  }

  show() {
    if (this.$modal) {
      this.$modal.open = true;
    }
  }

  addEventListeners() {
    // Await site-modal to be defined
    customElements.whenDefined('site-modal').then(() => {
      if (this.$modal) {
        this.$modal.dialog.addEventListener('close', this._emitClose);

        // Await marketo-form to be defined
        customElements.whenDefined('marketo-form').then(() => {
          if (this.$embedMarketo) {
            this.$embedMarketo.addEventListener('complete', this._emitComplete);
          }
        });
      }
    });
  }
}

customElements.define('promotion-modal', PromotionModal);

customElements.define('promotion-item', PromotionItem);
